import { create } from 'zustand';
import type {
  CardAccepted,
  ErrorCode,
  ErrorSummary,
  LevelStats,
  PrivatePlayerSnapshot,
  PublicPlayer,
  PublicRoomSnapshot,
  RewardSummary,
  SessionCreated,
  StarSummary,
} from '@sincronia/shared';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';

export type Toast = { code: ErrorCode; at: number };

export type Flash =
  | { kind: 'accepted'; payload: CardAccepted; at: number }
  | { kind: 'rejected'; cardId: string; code: ErrorCode; at: number }
  | { kind: 'error'; payload: ErrorSummary; at: number }
  | { kind: 'reward'; payload: RewardSummary; at: number }
  | { kind: 'star'; payload: StarSummary | null; approved: boolean; at: number }
  | { kind: 'level'; payload: LevelStats; at: number };

type GameState = {
  connection: ConnectionStatus;
  session: SessionCreated | null;
  snapshot: PublicRoomSnapshot | null;
  privateState: PrivatePlayerSnapshot | null;
  clockOffset: number;
  toast: Toast | null;
  flash: Flash | null;
  /** Carta enviada aguardando confirmação do servidor. */
  pendingCardId: string | null;
  publicAppUrl: string;

  setConnection: (status: ConnectionStatus) => void;
  setSession: (session: SessionCreated | null) => void;
  setSnapshot: (snapshot: PublicRoomSnapshot) => void;
  setPrivateState: (state: PrivatePlayerSnapshot) => void;
  setClockOffset: (offset: number) => void;
  setPublicAppUrl: (url: string) => void;
  setPendingCard: (cardId: string | null) => void;
  pushToast: (code: ErrorCode) => void;
  clearToast: () => void;
  setFlash: (flash: Flash | null) => void;
  reset: () => void;
};

export const useGameStore = create<GameState>((set) => ({
  connection: 'idle',
  session: null,
  snapshot: null,
  privateState: null,
  clockOffset: 0,
  toast: null,
  flash: null,
  pendingCardId: null,
  publicAppUrl: '',

  setConnection: (connection) => set({ connection }),
  setSession: (session) => set({ session }),
  setSnapshot: (snapshot) => set({ snapshot }),
  setPrivateState: (privateState) => set({ privateState }),
  setClockOffset: (clockOffset) => set({ clockOffset }),
  setPublicAppUrl: (publicAppUrl) => set({ publicAppUrl }),
  setPendingCard: (pendingCardId) => set({ pendingCardId }),
  pushToast: (code) => set({ toast: { code, at: Date.now() } }),
  clearToast: () => set({ toast: null }),
  setFlash: (flash) => set({ flash }),
  reset: () =>
    set({
      session: null,
      snapshot: null,
      privateState: null,
      toast: null,
      flash: null,
      pendingCardId: null,
    }),
}));

/* ----------------------------- Seletores ------------------------------- */

export function selectMe(state: GameState): PublicPlayer | null {
  const playerId = state.privateState?.playerId ?? state.session?.playerId;
  if (!playerId) return null;
  return state.snapshot?.players.find((player) => player.id === playerId) ?? null;
}

export function selectIsHost(state: GameState): boolean {
  const playerId = state.privateState?.playerId ?? state.session?.playerId;
  return Boolean(playerId && state.snapshot?.hostPlayerId === playerId);
}

/** Relógio do servidor estimado a partir do offset sincronizado. */
export function serverNow(): number {
  return Date.now() + useGameStore.getState().clockOffset;
}
